import React from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { LeaveAllocationRecord } from '../types';
import { format } from 'date-fns';

interface LeaveAllocationTableProps {
  allocations: LeaveAllocationRecord[];
  loading?: boolean;
  emptyState?: React.ReactNode;
  headerSlot?: React.ReactNode;
}

const formatTimestamp = (value?: any): string => {
  if (!value || typeof value.toDate !== 'function') return '-';
  return format(value.toDate(), 'dd MMM yyyy');
};

const AllocationProgressBar: React.FC<{
  approved: number;
  pending: number;
  allowance?: number;
}> = ({ approved, pending, allowance }) => {
  if (!allowance || allowance === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {approved}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          approved
        </Typography>
        {pending > 0 && (
          <>
            <Typography variant="caption" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2" color="warning.main" fontWeight={600}>
              {pending}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              pending
            </Typography>
          </>
        )}
      </Box>
    );
  }

  const approvedPercentage = (approved / allowance) * 100;
  const pendingPercentage = (pending / allowance) * 100;
  const totalPercentage = Math.min(approvedPercentage + pendingPercentage, 100);

  const remaining = Math.max(allowance - approved - pending, 0);
  const isOverAllocated = approved + pending > allowance;

  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="caption" display="block">
            Approved: {approved} days ({approvedPercentage.toFixed(0)}%)
          </Typography>
          <Typography variant="caption" display="block">
            Pending: {pending} days ({pendingPercentage.toFixed(0)}%)
          </Typography>
          <Typography variant="caption" display="block">
            Remaining: {remaining} days
          </Typography>
          <Typography variant="caption" display="block" fontWeight={600}>
            Total allowance: {allowance} days
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {approved}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            /
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {allowance}
          </Typography>
          {pending > 0 && (
            <Chip
              size="small"
              label={`+${pending} pending`}
              color="warning"
              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
            />
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 8,
            backgroundColor: 'action.hover',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${Math.min(approvedPercentage, 100)}%`,
              backgroundColor: isOverAllocated ? 'error.main' : 'success.main',
              transition: 'width 0.3s ease, background-color 0.3s ease',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: `${Math.min(approvedPercentage, 100)}%`,
              top: 0,
              height: '100%',
              width: `${Math.min(pendingPercentage, 100 - approvedPercentage)}%`,
              backgroundColor: 'warning.main',
              transition: 'width 0.3s ease, left 0.3s ease',
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};

export const LeaveAllocationTable: React.FC<LeaveAllocationTableProps> = ({
  allocations,
  loading = false,
  emptyState,
  headerSlot,
}) => {
  const hasAllocations = allocations.length > 0;

  const totalApproved = allocations.reduce((total, record) => total + (record.approvedDays || 0), 0);
  const totalPending = allocations.reduce((total, record) => total + (record.pendingDays || 0), 0);
  const totalDenied = allocations.reduce((total, record) => total + (record.deniedDays || 0), 0);

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      {headerSlot && (
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {headerSlot}
        </Box>
      )}

      {loading && <LinearProgress />}

      {!loading && !hasAllocations && (
        <Box sx={{ px: 3, py: 4 }}>
          {emptyState || (
            <Typography variant="body2" color="text.secondary">
              No leave allocation records found for this year yet.
            </Typography>
          )}
        </Box>
      )}

      {hasAllocations && (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Staff</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Allocation progress</TableCell>
                  <TableCell align="center">Denied</TableCell>
                  <TableCell>Last updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {record.staffName}
                      </Typography>
                      {record.coachId && (
                        <Typography variant="caption" color="text.secondary">
                          #{record.coachId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{record.location || '-'}</TableCell>
                    <TableCell>
                      <AllocationProgressBar
                        approved={record.approvedDays ?? 0}
                        pending={record.pendingDays ?? 0}
                        allowance={record.allowanceDays}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {record.deniedDays > 0 ? (
                        <Chip
                          size="small"
                          label={record.deniedDays}
                          color="error"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatTimestamp(record.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Approved days: <strong>{totalApproved}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending days: <strong>{totalPending}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Denied days: <strong>{totalDenied}</strong>
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default LeaveAllocationTable;
